/* eslint-disable eag/match-named-export -- Pre-existing grab-bag file with several exports */
import {match} from 'ts-pattern';

export type LineHeight = 'COMPACT' | 'SHORT' | 'DEFAULT' | 'TALL' | 'THUMBNAIL';

export const LineHeightToPx: Record<LineHeight, number> = {
  COMPACT: 28,
  SHORT: 32,
  DEFAULT: 36,
  TALL: 58,
  THUMBNAIL: 98,
};

export const HeaderHeightToPx: Record<LineHeight, number> = {
  COMPACT: 24,
  SHORT: 28,
  DEFAULT: 30,
  TALL: 48,
  THUMBNAIL: 80,
};

/**
 * Returns a Tailwind padding class appropriate for the given row/header height.
 * COMPACT, SHORT, and DEFAULT use reduced vertical padding so the row height takes effect;
 * otherwise the cell content (text + padding) would overflow the declared height.
 */
export const getRowPaddingClass: Record<LineHeight, string> = {
  COMPACT: 'py-0.5 px-2',
  SHORT: 'py-1 px-2',
  DEFAULT: 'py-1.5 px-2',
  TALL: 'p-2',
  THUMBNAIL: 'p-2',
};

/**
 * Total vertical padding (top + bottom) in pixels that corresponds to each
 * getRowPaddingClass entry.  Used by ThumbnailCellRenderer to shrink the image
 * so that image + padding = row height exactly.
 *   py-0.5  → 2 + 2 = 4px
 *   py-1    → 4 + 4 = 8px
 *   py-1.5  → 6 + 6 = 12px
 *   p-2     → 8 + 8 = 16px
 */
export const getRowVerticalPaddingPx: Record<LineHeight, number> = {
  COMPACT: 4,
  SHORT: 8,
  DEFAULT: 12,
  TALL: 16,
  THUMBNAIL: 16,
};

/** Mobile cell padding — 10px horizontal (touch spacing per the mobile mock),
 * vertical scaled by density like getRowPaddingClass. */
export const getMobileRowPaddingClass: Record<LineHeight, string> = {
  COMPACT: 'py-0.5 px-2.5',
  SHORT: 'py-1 px-2.5',
  DEFAULT: 'py-1.5 px-2.5',
  TALL: 'py-2 px-2.5',
  THUMBNAIL: 'py-2 px-2.5',
};

export type ButtonSizeBucket = 'small' | 'default';

/**
 * Maps a row/header LineHeight to the action-button size bucket.
 * COMPACT / SHORT / DEFAULT use the 'small' bucket; TALL / THUMBNAIL use 'default'.
 * Shared by getHeaderButtonWidth, getActionButtonSize and useGridActionColumn so the
 * bucketing predicate lives in one place.
 */
export const getButtonSizeBucket = (lineHeight: LineHeight): ButtonSizeBucket =>
  match(lineHeight)
    .with('COMPACT', 'SHORT', 'DEFAULT', () => 'small' as const)
    .with('TALL', 'THUMBNAIL', () => 'default' as const)
    .exhaustive();

/** Pixel size of an IconButton for each button-size bucket. */
export const BUTTON_SIZE_PX: Record<ButtonSizeBucket, number> = {
  small: 24,
  default: 32,
};
